/*
 * HONEYWELL INC.,
 * COPYRIGHT (c) 2022
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import React, { useContext } from 'react';
import { useTranslation } from '@scuf/localization';
import './PortfolioSummaryComponentTheme.scss';
import { Card, Tooltip } from '@scuf/common';

import descriptors from '../../../locales/en-US/descriptors';
import {
  isObjectOrArrayAndEmpty,
  returnFormattedDateTime,
  convertEuiContentCheck,
  convertEuiUnitCheck
} from '../../shared/Utils/Utils';
import { IAny, IEuiData, IRequestContext } from '../IEnergyManagementProps';
import { KPI_VALUE_DECIMAL_PRECISION } from '../../shared/Utils/Constants';
import {
  getEuiUnitDisplayName,
  handleEuiUnitConversion
} from '../../shared/Utils/unitHandling/EuiUnitHandling';
import { ApiContext } from '../../../context/ApiContext';
import { PollingContext } from '../../../context/PollingContext/PollingContext';
import { handleNumberUpscaling } from '../../shared/Utils/unitHandling/unitHandling';
import AccountWarning from '../../../images/AccountWarning.svg';
import { getCommonEUIContent } from '../../shared/Utils/EUI/EUIUtils';

interface IPortfolioSummaryContentProps {
  data?: IEuiData;
  message: string;
  requestContext: IRequestContext;
}

const PortfolioSummaryContent = (props: IPortfolioSummaryContentProps) => {
  const d = descriptors.energyManagement;
  const [t] = useTranslation();
  const apiContext = useContext(ApiContext);
  const {
    siteName,
    preferredEuiUnit,
    selectedDateFormat,
    selectedTimeFormat,
    totalFootprint,
    userPreferedUnit,
    totalFootPrintNativeUnit
  } = apiContext;

  const upscaledTotalFootprint = handleNumberUpscaling(
    totalFootprint || '',
    userPreferedUnit === 'kilowatt hour' ? 'square meter' : 'square foot',
    totalFootPrintNativeUnit
  );
  const footPrintUnit = userPreferedUnit === 'kilowatt hour' ? 'sqm' : 'sq.ft';

  const { data } = props;
  console.log('data', data);
  const errorMessage = '--';

  const renderTotalFootprint = (_areaPerSQft: number | string) => {
    return (
      <span className="emaas-sitedashboard-summary-value">{_areaPerSQft}</span>
    );
  };
  let date = new Date();
  date.setMonth(date.getMonth() - 1);
  const formattedDate = date.toLocaleDateString('en-US', {
    month: 'short',
    year: 'numeric'
  });
  const summaryContent = (
    _convertedEuiContent: string,
    _convertedEuiUnit: string,
    _areaPerSQft: number | string,
    footPrintUnit?: string
  ) => {
    return (
      <div className="emaas-sitedashboard-summary-values-container">
        {/* 2nd Column: Total Savings */}
        <div className="portfolio-summary-value-and-icon-container">
          <div className="emaas-sitedashboard-summary-value-and-label-container">
            <div className="emaas-sitedashboard-summary-valueWithPercentage">
              <div className="emaas-sitedashboard-summary-footprint-valueWithUnit eui">
                <span className="emaas-sitedashboard-summary-value">
                  {!!props.message && (
                    <Tooltip
                      element={
                        <img
                          className="img-account-warning"
                          src={AccountWarning}
                        />
                      }
                      event="hover"
                      content={props.message}
                      position="top center"
                      size="small"
                    />
                  )}
                  {convertEuiContentCheck(props.message, _convertedEuiContent)}
                </span>
                <span className="emaas-sitedashboard-summary-footprint-unit">
                  {convertEuiUnitCheck(props.message, _convertedEuiUnit)}
                </span>
              </div>
            </div>
            {getCommonEUIContent(
              t(d.EUITAB),
              t(d.SUMMARYEUITOOLTIPHEADERFORSITE, { date: formattedDate }),
              (
                <>
                  {t(d.SITESUMMARYEUITOOLTIP)}
                  <ul className="tooltip-text-for-site">
                    <strong>Note:</strong>
                    <ul>
                      <li>
                        EUI calculated without any extrapolation logic when last
                        12 months Utility Bill Consumption Data is available.
                      </li>
                      <li>
                        To calculate EUI, minimum 6 months data required in last
                        12 months and remaining months data will be linearly
                        extrapolated in case utility data not available.
                      </li>
                    </ul>
                  </ul>
                </>
              ) as IAny
            )}
          </div>
        </div>

        {/* 3rd Column: Total footprint */}
        <div className="portfolio-summary-value-and-icon-container">
          <div className="summary-image">
            <img
              src={require('../../../images/TotalFootprint_icon.svg')}
              alt=""
            />
          </div>

          <div className="emaas-sitedashboard-summary-value-and-label-container">
            <div className="emaas-sitedashboard-summary-footprint-valueWithUnit">
              {!!props.message && (!_areaPerSQft || _areaPerSQft === '--') && (
                <Tooltip
                  element={
                    <img className="img-account-warning" src={AccountWarning} />
                  }
                  event="hover"
                  content={props.message}
                  position="top center"
                  size="small"
                />
              )}
              {renderTotalFootprint(_areaPerSQft)}
              {_areaPerSQft !== '--' && (
                <span className="emaas-sitedashboard-summary-footprint-unit">
                  {footPrintUnit}
                </span>
              )}
            </div>
            <div className="emaas-sitedashboard-summary-label">
              {t(d.TOTALFOOTSPRINT)}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderErrorScenario = () => {
    return (
      <Card>
        <Card.Content>
          <div className="emaas-sitedashboard-summary-container-div">
            <div className="emaas-sitedashboard-summary-header">
              {siteName} - {t(d.PORTFOLIOSUMMARYHEADER)}
              {props.requestContext.context &&
                props.requestContext.context.endTime && (
                  <span className="emaas-sitedashboard-last-updated">
                    {t(d.PORTFOLIOSUMMARYHEADERLASTUPDATEDON, {
                      date: returnFormattedDateTime(
                        lastPollingTime,
                        selectedDateFormat,
                        selectedTimeFormat
                      )
                    })}
                  </span>
                )}
            </div>
            {console.log(
              'upscaledTotalFootprint',
              upscaledTotalFootprint,
              typeof upscaledTotalFootprint === 'number',
              Math.round(upscaledTotalFootprint * 10) / 10
            )}
            {summaryContent(
              errorMessage,
              '',
              // Math.round(upscaledTotalFootprint) || '--'
              typeof upscaledTotalFootprint === 'number'
                ? Math.round(upscaledTotalFootprint * 10) / 10 || '--'
                : upscaledTotalFootprint || '--'
              // upscaledTotalFootprint || '--'
            )}
          </div>
          {/* {console.log(
            Math.round(upscaledTotalFootprint * 10) / 10,
            'upscaledTotalFootprint2'
          )} */}
        </Card.Content>
      </Card>
    );
  };

  const apiData = data;

  if (!apiData || isObjectOrArrayAndEmpty(apiData)) {
    return renderErrorScenario();
  }

  const { euiMeasurementUnit, siteWiseCalculation } = apiData;

  let actualEui = 0;

  if (siteWiseCalculation && siteWiseCalculation[0]) {
    actualEui = siteWiseCalculation[0].actualEui;
  }

  const convertedEuiContent = `${parseFloat(
    handleEuiUnitConversion(
      actualEui,
      euiMeasurementUnit,
      preferredEuiUnit || ''
    ).toFixed(KPI_VALUE_DECIMAL_PRECISION)
  )}`;

  const convertedEuiUnit = getEuiUnitDisplayName(preferredEuiUnit);

  const pollingContext = useContext(PollingContext);
  const { lastPollingTime } = pollingContext;

  return (
    <Card>
      <Card.Content>
        <div className="emaas-sitedashboard-summary-container-div">
          <div className="emaas-sitedashboard-summary-header">
            {siteName} - {t(d.PORTFOLIOSUMMARYHEADER)}
            {props.requestContext.context &&
              props.requestContext.context.endTime && (
                <span className="emaas-sitedashboard-last-updated">
                  {t(d.PORTFOLIOSUMMARYHEADERLASTUPDATEDON, {
                    date: returnFormattedDateTime(
                      lastPollingTime,
                      selectedDateFormat,
                      selectedTimeFormat
                    )
                  })}
                </span>
              )}
          </div>
          {console.log(
            'upscaledTotalFootprint3',
            upscaledTotalFootprint,
            Math.round(upscaledTotalFootprint * 10) / 10
          )}
          {summaryContent(
            convertedEuiContent,
            convertedEuiUnit,
            typeof upscaledTotalFootprint === 'number'
              ? Math.round(upscaledTotalFootprint * 10) / 10 || '--'
              : upscaledTotalFootprint || '--',
            // upscaledTotalFootprint || '--',
            footPrintUnit
          )}
          {/* {console.log(
            Math.round(upscaledTotalFootprint * 10) / 10 || '--',
            'upscaledTotalFootprint4'
          )} */}
        </div>
      </Card.Content>
    </Card>
  );
};

export default PortfolioSummaryContent;
